/**
 * TelegramBootstrap — Phase 3 patch 3.2.
 *
 * Component vô hình. Chạy 1 lần khi app mount để:
 *   1. Detect TMA mode (window.Telegram.WebApp.initData không rỗng).
 *   2. initTelegramApp(): ready() + expand() fullscreen.
 *   3. Auto-set locale theo Telegram language_code (nếu user chưa chọn).
 *   4. Auto-auth: nếu chưa có JWT và đang trong TMA → POST initData lên
 *      backend → nhận token → redirect tới flow phù hợp.
 *   5. Apply Telegram themeParams (bg/text color) qua CSS variables.
 *   6. Enable close confirmation khi đang ở session active (Phase 4).
 *
 * Idempotent — chạy nhiều lần (StrictMode) không gây side-effect kép.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { getAccessToken } from "../lib/api";
import {
  enableCloseConfirmation,
  getInitData,
  getTelegramLanguageCode,
  getThemeParams,
  initTelegramApp,
  isTelegramMiniApp,
  mapTelegramLangToLocale,
} from "../lib/telegram";
import { LOCALE_STORAGE_KEY, setLocale } from "../i18n";
import { hasChosenLocale } from "../screens/LanguageSelector";
import { useShopStore } from "../stores/shopStore";

const LOCALE_CHOSEN_KEY = "cafechuquan_locale_chosen";

function applyTelegramTheme(): void {
  const theme = getThemeParams();
  const root = document.documentElement;
  // Set CSS variables — components có thể opt-in dùng var(--tg-bg-color).
  if (theme.bg_color) root.style.setProperty("--tg-bg-color", theme.bg_color);
  if (theme.text_color) root.style.setProperty("--tg-text-color", theme.text_color);
  if (theme.hint_color) root.style.setProperty("--tg-hint-color", theme.hint_color);
  if (theme.button_color) root.style.setProperty("--tg-button-color", theme.button_color);
  if (theme.button_text_color) {
    root.style.setProperty("--tg-button-text-color", theme.button_text_color);
  }
}

export function TelegramBootstrap() {
  const navigate = useNavigate();
  const telegramAuth = useShopStore((s) => s.telegramAuth);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    // 1. Init SDK + theme. Safe to call ngoài Telegram (no-op).
    initTelegramApp();
    applyTelegramTheme();

    if (!isTelegramMiniApp()) {
      // Trình duyệt thường — không làm gì, để flow login/language bình thường.
      return;
    }

    // 2. Auto-set locale theo Telegram user language (nếu user chưa chọn locale).
    if (!hasChosenLocale()) {
      const tgLang = getTelegramLanguageCode();
      const targetLocale = mapTelegramLangToLocale(tgLang);
      setLocale(targetLocale);
      try {
        localStorage.setItem(LOCALE_CHOSEN_KEY, "true");
        localStorage.setItem(LOCALE_STORAGE_KEY, targetLocale);
      } catch {
        // SSR / private mode — ignore
      }
    }

    // 3. Enable close confirmation (chống user vô tình đóng giữa session).
    enableCloseConfirmation();

    // 4. Auto-auth nếu chưa có token.
    if (getAccessToken()) {
      return;
    }
    const initData = getInitData();
    if (!initData) return;

    telegramAuth(initData)
      .then((result) => {
        // User mới → vào IntroScreen story.
        // User cũ → / (sẽ tự navigate tới /onboarding/intro nếu chưa đặt tên).
        if (result.isNewUser) {
          navigate("/onboarding/intro", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      })
      .catch((err) => {
        // Log nhưng không alert — TMA có thể fail vì init_data expired hoặc
        // server downtime; user thấy LoginScreen như bình thường.
        console.error("Telegram auth failed:", err);
      });
  }, [navigate, telegramAuth]);

  return null;
}
